//---------------------------------------------
import React, { useEffect, useState } from "react";
import ThemeSelector from "./components/ThemeSelector";
import PredefinedThemesList from "./components/PredefinedThemesList";
import CustomThemeEditor from "./components/CustomThemeEditor";
import { predefinedThemes } from "./components/data";
import { useAppServices } from "../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

const ThemeBuilderV2 = () => {
  const { companyId } = useParams();
  const AppService = useAppServices();
  const defaultTheme = predefinedThemes[0];

  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState("Default");
  const [customTheme, setCustomTheme] = useState(defaultTheme.colors);
  const [themeData, setThemeData] = useState(null);
  const [saving, setSaving] = useState(false);

  const getCustomization = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.theme_customization.filter({
        query: `companyId=${companyId}`,
      });
      if (response) {
        const themeType = response.data?.themeType;
        const themeId = response.data?.themeId;
        let intialSelectedTheme =
          themeType == "predefined" ? themeId : themeType;
        setSelectedTheme(intialSelectedTheme);
        if (themeType == "Custom") {
          setCustomTheme(response.data?.configuration);
        }
        setThemeData(response.data);
      }
    } catch (error) {
      setSelectedTheme("Default");
      setThemeData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomization();
  }, []);

  const handleColorChange = (colorType, value) => {
    setCustomTheme((prev) => ({ ...prev, [colorType]: value }));
  };

  const handleThemeSelection = (themeId) => {
    setSelectedTheme(themeId);

    if (themeId !== "Custom" && themeId !== "Default") {
      const theme = predefinedThemes.find((t) => t.id === themeId);
      if (theme) setCustomTheme(theme.colors);
    }
  };

  const getThemeColors = () => {
    if (selectedTheme === "Custom") return customTheme;
    if (selectedTheme === "Default") return defaultTheme.colors;
    const theme = predefinedThemes.find((t) => t.id === selectedTheme);
    return theme.colors;
  };

  const colors = getThemeColors();

  const handleSave = async () => {
    setSaving(true);
    try {
      const theme = selectedTheme === "Custom" ? customTheme : getThemeColors();
      const payload = {
        companyId,
        themeType:
          selectedTheme != "Custom" && selectedTheme != "Default"
            ? "predefined"
            : selectedTheme,
        configuration: theme,
        themeId:
          selectedTheme != "Custom" && selectedTheme != "Default"
            ? selectedTheme
            : null,
      };
      if (themeData?._id) {
        payload["_id"] = themeData?._id;
      }
      const { response } = await AppService.theme_customization[
        themeData?._id ? "update" : "create"
      ]({
        payload,
      });
      if (response) {
        toast.success("Theme updated successfully.");
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tb-page d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border spinner-border-sm mb-2 text-secondary" role="status" aria-hidden="true" />
        <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>Loading themes…</span>
        <TbStyles />
      </div>
    );
  }

  const presetTheme = predefinedThemes.find((t) => t.id === selectedTheme);
  const modeLabel =
    selectedTheme === "Custom"
      ? "Custom"
      : selectedTheme === "Default"
      ? "Default"
      : presetTheme?.name || "Preset";

  // Collect up to 5 unique color values for the live preview swatches
  const swatches = Array.from(
    new Set(
      Object.values(colors).filter(
        (v) => typeof v === "string" && v.trim().startsWith("#")
      )
    )
  ).slice(0, 5);

  return (
    <div className="tb-page">
      <div className="container-fluid py-4 px-4 pb-5">
        {/* Hero */}
        <section className="tb-hero">
          <div className="tb-hero-bg" />
          <div className="tb-hero-inner d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
              <span className="tb-hero-icon d-inline-flex align-items-center justify-content-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="13.5" cy="6.5" r=".5" />
                  <circle cx="17.5" cy="10.5" r=".5" />
                  <circle cx="8.5" cy="7.5" r=".5" />
                  <circle cx="6.5" cy="12.5" r=".5" />
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
                </svg>
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="tb-hero-eyebrow">Theme builder</div>
                <h3 className="tb-hero-title mb-0">{modeLabel}</h3>
                <p className="tb-hero-subtitle mb-0">
                  Choose a preset or craft a custom color theme for your knowledgebase.
                </p>
              </div>
            </div>

            <div className="tb-preview-card">
              <div className="tb-preview-label">Live preview</div>
              <div className="d-flex align-items-center gap-2 mt-2">
                {swatches.length > 0 ? (
                  swatches.map((c, i) => (
                    <span
                      key={`${c}-${i}`}
                      className="tb-preview-swatch"
                      style={{ background: c }}
                      title={c}
                    />
                  ))
                ) : (
                  <span className="tb-preview-empty">No colors yet</span>
                )}
              </div>
              <button
                type="button"
                className="tb-preview-btn mt-3"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  backgroundColor: colors["--color-accent-yellow"],
                  borderColor: colors["--color-accent-yellow"],
                  color: colors["--button-text-color"],
                }}
              >
                Sample button
              </button>
            </div>
          </div>
        </section>

        {/* Section: theme selector */}
        <div className="tb-section-head mt-4 mb-2">
          <div className="tb-section-eyebrow">Step 1</div>
          <h6 className="tb-section-title mb-0">Pick your theme source</h6>
        </div>
        <ThemeSelector
          selectedTheme={selectedTheme}
          onSelect={handleThemeSelection}
        />

        {/* Section: presets or custom editor */}
        <div className="tb-section-head mt-4 mb-2">
          <div className="tb-section-eyebrow">Step 2</div>
          <h6 className="tb-section-title mb-0">
            {selectedTheme === "Custom" ? "Customize colors" : "Choose a preset"}
          </h6>
        </div>

        {selectedTheme !== "Custom" && (
          <PredefinedThemesList
            predefinedThemes={predefinedThemes}
            selectedTheme={selectedTheme}
            onSelect={handleThemeSelection}
          />
        )}

        {selectedTheme === "Custom" && (
          <CustomThemeEditor
            customTheme={customTheme}
            onChange={handleColorChange}
          />
        )}

        {/* Action footer (in-flow, not sticky — avoids overlap with onboarding pill) */}
        <div className="tb-action-bar mt-4">
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <div className="tb-action-info d-flex align-items-center gap-2">
              <span className="tb-action-dot" style={{ background: colors["--color-accent-yellow"] || "#4f46e5" }} />
              <span className="tb-action-label">
                Active theme: <strong>{modeLabel}</strong>
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="tb-btn tb-btn--ghost"
                onClick={() => {
                  setSelectedTheme("Default");
                  setCustomTheme(defaultTheme.colors);
                }}
                disabled={saving}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                Reset
              </button>

              <button
                type="button"
                className="tb-btn tb-btn--primary"
                onClick={handleSave}
                disabled={saving}
                style={{
                  backgroundColor: colors["--color-accent-yellow"],
                  borderColor: colors["--color-accent-yellow"],
                  color: colors["--button-text-color"],
                }}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Saving
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Save theme
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Trailing space so the onboarding pill (bottom-right floater) never sits on top of the action footer */}
        <div style={{ height: 80 }} aria-hidden />
      </div>

      <TbStyles />
    </div>
  );
};

const TbStyles = () => (
  <style>{`
    .tb-page {
      font-family: 'Inter', sans-serif;
      background: #f7f7fb;
      min-height: 100vh;
    }

    /* Hero */
    .tb-hero {
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      background: #fff;
      border: 1px solid #ececec;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .tb-hero-bg {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(900px 200px at -10% -40%, rgba(79, 70, 229, 0.10), transparent 60%),
        radial-gradient(700px 220px at 110% 120%, rgba(99, 102, 241, 0.10), transparent 60%),
        linear-gradient(180deg, rgba(79, 70, 229, 0.03), rgba(79, 70, 229, 0));
      pointer-events: none;
    }
    .tb-hero-inner {
      position: relative;
      padding: 22px 24px;
    }
    .tb-hero-icon {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
      color: #4f46e5;
      flex-shrink: 0;
      border: 1px solid #e0e7ff;
      box-shadow: 0 4px 14px rgba(79, 70, 229, 0.12);
    }
    .tb-hero-eyebrow {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #4f46e5;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .tb-hero-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: -0.02em;
      line-height: 1.15;
    }
    .tb-hero-subtitle {
      font-size: 0.8125rem;
      color: #6b7280;
      line-height: 1.5;
      margin-top: 4px;
      max-width: 540px;
    }

    /* Live preview card */
    .tb-preview-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      padding: 12px 14px;
      min-width: 220px;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .tb-preview-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #6b7280;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }
    .tb-preview-swatch {
      width: 22px;
      height: 22px;
      border-radius: 7px;
      border: 1px solid rgba(0, 0, 0, 0.06);
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.06);
    }
    .tb-preview-empty {
      font-size: 0.75rem;
      color: #9ca3af;
    }
    .tb-preview-btn {
      width: 100%;
      padding: 7px 10px;
      border-radius: 8px;
      border: 1px solid transparent;
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1;
      cursor: default;
      pointer-events: none;
      font-family: inherit;
      transition: filter 0.15s ease;
    }

    /* Section heads */
    .tb-section-head {
      padding: 0 4px;
    }
    .tb-section-eyebrow {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #4f46e5;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .tb-section-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #0f172a;
      letter-spacing: -0.005em;
      line-height: 1.3;
    }

    /* Action footer (in normal flow, not sticky) */
    .tb-action-bar {
      padding: 16px 20px;
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 14px;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .tb-action-info {
      font-size: 0.8125rem;
      color: #6b7280;
    }
    .tb-action-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.12);
      flex-shrink: 0;
    }
    .tb-action-label strong {
      color: #1f2937;
      font-weight: 600;
    }

    /* Buttons */
    .tb-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 9px 18px;
      border-radius: 9px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, filter 0.15s ease;
    }
    .tb-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none !important; }
    .tb-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }
    .tb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .tb-btn--ghost:hover:not(:disabled) {
      background: #fafafa;
      border-color: #d1d5db;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
    }
    .tb-btn--ghost:active:not(:disabled) { transform: translateY(0); }
    .tb-btn--primary {
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.2) inset,
        0 1px 2px rgba(17, 24, 39, 0.12),
        0 4px 10px rgba(17, 24, 39, 0.08);
    }
    .tb-btn--primary:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(0.95);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.2) inset,
        0 2px 4px rgba(17, 24, 39, 0.16),
        0 8px 18px rgba(17, 24, 39, 0.14);
    }
    .tb-btn--primary:active:not(:disabled) { transform: translateY(0); }
  `}</style>
);

export default ThemeBuilderV2;
